import { classnames } from 'tailwindcss-classnames';
import Button from 'components/helpers/Button';
import useDictionary from 'lib/sitecore/use-dictionary';
import { Sitecore } from '.generated/templates/_.Sitecore.Override';
import { Routes } from '.generated/templates/Project.Routes.model';
import { Data } from '.generated/templates/Feature.Data.model';
import {
  addToCalendar,
  getFormattedEndTime,
  getFormattedEventDate,
  getFormattedStartTime,
} from 'lib/util/event';
import getThemeClasses from 'lib/util/theming';
import ImageWrapper from '../ImageWrapper/ImageWrapper';

import { getFeaturedImageCaption, getLinkAndHeaderProps, PageCardProps } from './card-utils';
import VideoItem, { VideoItemProps } from 'components/helpers/VideoItem/VideoItem';
import { ImageField, useSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import CardHeading from './cardHeading';

export type EventPage = Sitecore.Override.ItemEx &
  Routes.Fields.EventPage & {
    fields: {
      eventCategory?: Data.Events.Fields.EventCategory[];
      pageFeaturedVideo?: VideoItemProps;
    };
  };

const EventPageCard = (props: PageCardProps): React.JSX.Element => {
  const dictionary = useDictionary();
  const { sitecoreContext } = useSitecoreContext();

  const { promotedPage, isSingleCard, onGtmPush } = props;
  if (!promotedPage) {
    return <></>;
  }
  const eventDateHeading = dictionary.Event_Date;
  const eventLocationHeading = dictionary.Event_Location;
  const eventTimeHeading = dictionary.Event_Time;
  const addToCalendarText = dictionary.Add_To_Calendar;

  const eventPage = promotedPage as EventPage;

  const featuredImage = eventPage.fields.pageFeaturedImage.value?.src
    ? eventPage.fields.pageFeaturedImage
    : (sitecoreContext.defaultFeaturedImage as ImageField);

  const imageCaption = getFeaturedImageCaption(promotedPage.fields.featuredImageCaption);
  const cardEyebrowClasses = classnames('text-sm', 'font-semibold');

  const { linkField, headingProps } = getLinkAndHeaderProps(props, false);

  const formattedEventDate = getFormattedEventDate(
    eventPage.fields.startDateTime.value,
    eventPage.fields.endDateTime.value
  );

  const formattedStartTime = getFormattedStartTime(
    eventPage.fields.startDateTime.value,
    eventPage.fields.endDateTime.value
  );
  const formattedEndTime = getFormattedEndTime(eventPage.fields.endDateTime.value);

  const description = eventPage.fields.pageDescription.value;

  const addToCalendarHandler = async () => {
    addToCalendar(
      eventPage.fields.pageTitle.value,
      eventPage.fields.pageDescription.value,
      eventPage.fields.startDateTime.value,
      eventPage.fields.endDateTime.value,
      eventPage.fields.location.value,
      eventPage.fields.eventCategory
    );
  };
  const renderEventLine = (label: string, info: string) => {
    if (!info) {
      return <></>;
    }
    return (
      <li className={classnames('place-content-between', 'mb-2', 'flex', 'w-full', 'flex-row')}>
        <span
          className={classnames(
            'block',
            'basis-1/4',
            'font-semibold',
            'leading-3',
            'text-blue-navy'
          )}
        >
          {label}
        </span>
        <span
          className={classnames(
            'w-1/2',
            'block',
            'basis-3/4',
            'shrink-0',
            'whitespace-pre-line',
            'break-words'
          )}
        >
          {info}
        </span>
      </li>
    );
  };

  const eventCategories = eventPage.fields.eventCategory
    .map((item: unknown) => {
      const eventCategory = item as Data.Events.Fields.EventCategory;
      return eventCategory.fields?.eventCategoryTitle.value;
    })
    .filter(Boolean)
    .join(', ');

  return (
    <>
      <div className={classnames('callout-card-img')}>
        {!eventPage.fields.pageFeaturedVideo ? (
          <ImageWrapper field={featuredImage} layout="responsive" />
        ) : (
          <div
            className={classnames(
              'col-span-2',
              'md:col-span-1',
              'lg:col-span-2',
              'md:grid-cols-2',
              'lg:grid-cols-3'
            )}
          >
            <VideoItem {...eventPage.fields.pageFeaturedVideo} hideVideoContent />
          </div>
        )}
      </div>
      <div
        className={classnames('callout-card-content', 'max-h-full', getThemeClasses('theme-white'))}
      >
        <div>
          <CardHeading
            headingProps={headingProps}
            cardEyebrow={eventCategories}
            cardEyebrowClasses={cardEyebrowClasses}
            imageCaption={imageCaption}
            isSingleCard={isSingleCard}
            descriptionDisabled
          />
          {description && <div className={classnames('mb-4')}>{description}</div>}
          <ul
            className={classnames(
              'mb-8',
              'shrink-0',
              'grow',
              'rounded-lg',
              'border',
              'border-solid',
              'border-gray-quartz',
              'p-4',
              'md:mb-6'
            )}
          >
            {renderEventLine(eventDateHeading, formattedEventDate)}
            {renderEventLine(eventTimeHeading, `${formattedStartTime}${formattedEndTime}`)}
            {renderEventLine(eventLocationHeading, eventPage.fields.location.value)}
          </ul>
        </div>

        <div className={classnames('cta-container')}>
          <Button linkField={linkField} onGtmPush={onGtmPush} />
          <Button text={addToCalendarText} variant="secondary" onClick={addToCalendarHandler} />
        </div>
      </div>
    </>
  );
};

export default EventPageCard;
